import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isEmptyArray, isNonEmptyArray, NotificationType, removeNotification, toggleSeatSelection } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { AnimatePresence } from 'framer-motion';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef } from 'react';
import { useSeatByIdMap } from '../../hooks/use-seat-by-id-map';
import { Notification } from '../../subcomponents/notification/notification';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { SeatPickerLegend } from './subcomponents/legend';
import { SeatPickerZoomController } from './subcomponents/zoom-controller';
import { mapSeatPickerContext, SeatPickerContextInstance, SeatPickerSelectedSeatsContextInstance } from './types/context';
import { defaultSeatPickerProps, seatPickerPropsValidator } from './types/props';
import { defaultSeatPickerState } from './types/state';
import { seatPickerActionReducer } from './utils/action-reducer';
import { SeatPickerActionType } from './utils/action-type';
import { getSeatToAppearanceMapForRules } from './utils/get-seat-to-appearance-map-for-rules';
import { getUsedSeatAppearanceRules } from './utils/get-used-seat-appearance-rules';
import { validateSeatSelections } from './utils/validate-seat-selections';
const b = bemClassBuilder('v-seat-picker');
const invalidSelectionNotificationId = getIdForComponentNotification('seat-picker', NotificationType.Error, 'invalid-selection');
const SeatPickerBase = (props, ref) => {
    const { data, config, displayText, events, selectedSeatIds, isDisabled = false } = props;
    const { onSeatClicked, onSelectedSeatsChanged } = events;
    const [state, dispatch] = useReducer(seatPickerActionReducer, defaultSeatPickerState);
    const seatByIdMap = useSeatByIdMap(data.seatLayout);
    const selectedSeats = selectedSeatIds.map(id => {
        const seat = seatByIdMap.get(id);
        if (!seat)
            throw new Error(`Seat with ID ${id} could not be found.`);
        return seat;
    });
    const seatIdToAppearance = useMemo(() => getSeatToAppearanceMapForRules(config.seats.appearanceRules, data), [config.seats.appearanceRules, data]);
    const usedAppearanceRuleIds = useMemo(() => getUsedSeatAppearanceRules(data, seatIdToAppearance), [data, seatIdToAppearance]);
    useImperativeHandle(ref, () => ({
        validateSeatSelections: () => {
            const result = validateSeatSelections(config.selectionRules, displayText, data, selectedSeats);
            dispatch({ type: SeatPickerActionType.SetValidationErrorsAction, violatedRules: result.violatedRules });
            return result;
        }
    }));
    // Use useRef to prevent the handleSeatClicked callback from changing when the selectedSeats prop changes
    // https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
    const selectedSeatsRef = useRef(selectedSeats);
    selectedSeatsRef.current = selectedSeats;
    const handleSeatClicked = useCallback(async (seatDetails) => {
        if (isDisabled)
            return;
        // If onSeatClicked has been provided and return a false response, bail out of the handler.
        if (onSeatClicked) {
            const response = await onSeatClicked(seatDetails);
            if (response === false)
                return;
        }
        if (!onSelectedSeatsChanged)
            return;
        const updatedSelectedSeats = toggleSeatSelection(selectedSeatsRef.current, data.seatSelectionLimits, seatDetails);
        if (updatedSelectedSeats !== undefined) {
            onSelectedSeatsChanged(updatedSelectedSeats);
            dispatch({ type: SeatPickerActionType.SetValidationErrorsAction, violatedRules: [] });
        }
    }, [isDisabled, onSeatClicked, onSelectedSeatsChanged, data.seatSelectionLimits]);
    const context = useMemo(() => mapSeatPickerContext(props, handleSeatClicked, seatIdToAppearance), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        props.assets,
        props.config,
        props.displayText,
        props.data,
        props.isDisabled,
        handleSeatClicked,
        seatIdToAppearance
    ]);
    useEffect(() => {
        if (config.notification.displayInline)
            return;
        if (isEmptyArray(state.validationErrorMessages))
            removeNotification(invalidSelectionNotificationId);
    }, [config.notification.displayInline, state.validationErrorMessages]);
    return (_jsx(SeatPickerContextInstance.Provider, Object.assign({ value: context }, { children: _jsx(SeatPickerSelectedSeatsContextInstance.Provider, Object.assign({ value: selectedSeats }, { children: _jsxs("div", Object.assign({ className: b.block() }, { children: [_jsx("div", Object.assign({ className: b.element('legend', { [config.legend.position]: true }) }, { children: _jsx(SeatPickerLegend, { usedAppearanceRuleIds: usedAppearanceRuleIds }) })), _jsx("div", Object.assign({ className: b.element('map') }, { children: _jsx(SeatPickerZoomController, {}) })), _jsx(AnimatePresence, { children: config.notification.show && isNonEmptyArray(state.validationErrorMessages) && (_jsx(Notification, { id: invalidSelectionNotificationId, messages: state.validationErrorMessages, type: config.notification.style, wrapperClassName: b.element('notification'), displayInline: config.notification.displayInline })) })] })) })) })));
};
export const SeatPicker = forwardRef(SeatPickerBase);
SeatPicker.displayName = 'SeatPicker';
/**
 * The **Seat Picker** component renders a seat map and legend. It allows a user to select seats, and validates the
 * user's seat selection against seating rules.
 *
 * The **Seat Picker** component uses SVG icons to render seats. A default icon set is included, but custom icons can
 * be specified in the configuration.
 */
export const SeatPickerComponent = withComponentWrapper(SeatPicker, defaultSeatPickerProps, seatPickerPropsValidator);
